/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Component, ComponentChild, ComponentType, VNode } from 'preact';
import { TransitionProps } from './Transition';
import { ChildKey, ChildMapping, TransitionUtils } from './TransitionUtils';

/**
 * Props for the TransitionGroup Component
 */
type TransitionGroupProps = {
  /**
   * @description
   * <TransitionGroup> renders a '<div>' by default. You can change this
   * behavior by providing a different ComponentType value to this prop.
   */
  elementType?: 'div' | ComponentType;

  /**
   * @description
   * Children component should be a collection of TransitionComponent. The child
   * components should be a Transition Component.
   */
  children: VNode<TransitionProps>[];
};

/**
 * State for the TransitionGroup component
 */
type State = {
  childMapping?: ChildMapping;
  handleExited?: (child: VNode<TransitionProps>, node: Element, key?: ChildKey) => void;
};

/**
 * @classdesc
 * The <TransitionGroup> component manages a set of components that involves animations.
 * This component does not handle any animation, rather just a state machine that manages
 * the mounting and unmounting of the components over the time. The actual animation needs
 * to be handled by the content component.
 *
 * Consider the example below:
 * <TransitionGroup>
 *   {
 *      messages.map(message => {
 *        <Transition key={message.key}>
 *          <Message
 *            type={type}
 *            index={index}
 *            item={data.message}
 *            onOjClose={onOjClose}
 *          />
 *        </Transition>
 *      });
 *   }
 * </TransitionGroup>
 * As the messages are added/removed, the TransitionGroup Component automatically
 * toggles the 'in' prop of the Transition Component.
 *
 * @ignore
 */
export class TransitionGroup extends Component<TransitionGroupProps, State> {
  static defaultProps: Partial<TransitionGroupProps> = {
    elementType: 'div'
  };

  /**
   * Derives state from the current props
   *
   * @param props The current Props that will be used to get the new state
   * @param state The current state
   *
   * @returns The new state
   */
  static getDerivedStateFromProps(
    props: Readonly<TransitionGroupProps>,
    state: Readonly<State>
  ): State | null {
    const { childMapping, handleExited } = state;
    return {
      childMapping: TransitionUtils.getChildMapping(props.children, childMapping, handleExited)
    };
  }

  private _mounted: boolean;

  ////////////////////////////////////////////////////////////////////////
  // Handler functions are created as members to have them 'this' bound //
  ////////////////////////////////////////////////////////////////////////

  /**
   * Handles when a transition component exits
   *
   * @param child The child instance that exited
   * @param node The corresponding transition element
   * @param metadata The metadata of the corresponding transition component
   */
  private readonly _handleExited = (
    child: VNode<TransitionProps>,
    node?: Element,
    metadata?: any
  ) => {
    const { children } = this.props;
    // get the child mapping for the current children
    const currentChildMapping = TransitionUtils.getChildMapping(children);

    // if the exited child is added again, do nothing here
    if (currentChildMapping.has(child.key)) return;

    // The child component has exited, call the original onExited callback
    child.props.onExited?.(node, metadata);

    // Check if this component is still mounted, if so update the state
    if (this._mounted) {
      this.setState((state: Readonly<State>): Partial<State> => {
        const childMapping = new Map(state.childMapping!);
        // delete the exited child
        childMapping.delete(child.key);
        return { childMapping };
      });
    }
  };

  ///////////////////////////
  // Handler functions end //
  ///////////////////////////

  /**
   * Instantiates Component
   *
   * @param props The component properties
   */
  constructor(props: Readonly<TransitionGroupProps>) {
    super(props);

    this.state = {
      childMapping: undefined,
      handleExited: this._handleExited
    };
    this._mounted = false;
  }

  //////////////////////////////////////
  // Component Life Cycle Hooks Start //
  //////////////////////////////////////

  /**
   * Life cycle hook that gets called when the component is mounted on to
   * the DOM
   */
  componentDidMount() {
    this._mounted = true;
  }

  /**
   * Life cycle hook that gets called when the component is unmounted from
   * the DOM
   */
  componentWillUnmount() {
    this._mounted = false;
  }

  ////////////////////////////////////
  // Component Life Cycle Hooks End //
  ////////////////////////////////////

  /**
   * Renders the transition components
   */
  render(): ComponentChild {
    const WrapperComponent = this.props.elementType!;
    const { childMapping } = this.state;
    const children = [...childMapping!.values()];
    return <WrapperComponent>{children}</WrapperComponent>;
  }
}
